import { useCallback, useContext, useEffect, useState } from 'react';
import { toast } from 'sonner';
import type { SupplyAveragePrice } from '@obraplan/core/application/ports/requisition';
import { messageForCode } from '@obraplan/core/application/errors/error-codes';
import { PERMISSION } from '@obraplan/core/domain/access/permissions';
import type { ProjectId } from '@obraplan/core/domain/shared/ids';
import type { ProductUnitFactor } from '@obraplan/core/domain/takeoff/material-price';
import { ContainerContext, SessionContext } from '@/features/session/session-context';
import {
  mapaDeFatores,
  mapaDoCompras,
  pedidoVazio,
  type ConsultaParaOrcar,
  type Lido,
  type PedidoParaOrcar,
  type ProdutosDaImportacao,
} from './completar-para-orcar';
import { periodoDeDozeMeses } from './preco-do-compras';
import { emLotes, mapaDePrecos } from './precos-da-obra';
import { avisarPrecosGravados } from './use-precos-da-obra';

/**
 * A consulta e a gravação do "Completar para orçar" da importação (13/10/2026).
 *
 * Ao abrir a prévia, três leituras: o compras (a unidade e o médio de cada código, nos doze
 * meses), os fatores que os produtos já têm e os preços desta obra. Na confirmação, depois de a
 * importação ter sido despachada, uma gravação dos fatores — para todas as obras — e um
 * provisório por produto sem código.
 *
 * ## Sem obra, sem sessão ou sem permissão, o passo não existe
 *
 * O contexto é lido sem exigir o provedor: a importação também é montada sozinha (no teste, e
 * na tela que ainda não recebeu a obra), e ali ela continua importando como sempre, sem o passo.
 * Quem só vê o quantitativo não grava fator nem preço (D13), então também não é perguntado.
 *
 * ## A importação vale mesmo se a gravação falhar
 *
 * O catálogo e as quantidades vão para o rascunho antes; o fator e o provisório são outra
 * gravação, no servidor. A falha vira aviso — e o que faltou se completa no preço do item.
 */

export type EstadoDaConsulta = 'INDISPONIVEL' | 'LENDO' | 'PRONTA';

export interface CompletarParaOrcarNaTela {
  readonly estado: EstadoDaConsulta;
  readonly consulta?: ConsultaParaOrcar;
  /** Grava o pedido; não lança — a falha vira aviso, e a importação continua aplicada. */
  readonly gravar: (pedido: PedidoParaOrcar) => Promise<void>;
}

const SEM_CONEXAO = 'verifique a conexão.';

const quantos = (n: number, um: string, varios: string): string =>
  `${String(n)} ${n === 1 ? um : varios}`;

export function useCompletarParaOrcar(
  produtos: ProdutosDaImportacao | undefined,
  projectId: ProjectId | undefined,
): CompletarParaOrcarNaTela {
  const container = useContext(ContainerContext);
  const session = useContext(SessionContext)?.session;
  // Nasce uma vez com a prévia: virar a meia-noite com ela aberta não refaz a pergunta.
  const [periodo] = useState(() => periodoDeDozeMeses(new Date()));
  const [lida, setLida] = useState<
    { readonly de: ProdutosDaImportacao; readonly consulta: ConsultaParaOrcar } | undefined
  >(undefined);

  const disponivel =
    produtos !== undefined &&
    container !== undefined &&
    session !== undefined &&
    projectId !== undefined &&
    container.permissions.can(session, PERMISSION.TAKEOFF_MANAGE, projectId);
  // Só a mão de obra não pergunta nada ao servidor: sem material, não há o que ler.
  const temMaterial =
    produtos !== undefined && produtos.comCodigo.length + produtos.semCodigo.length > 0;

  useEffect(() => {
    if (!disponivel || !temMaterial) return undefined;
    // `disponivel` já garante os quatro; o TypeScript não acompanha o booleano.
    if (
      produtos === undefined ||
      container === undefined ||
      session === undefined ||
      projectId === undefined
    ) {
      return undefined;
    }
    let vivo = true;
    const daVez = produtos;
    const codes = produtos.codigos;

    const lerCompras = async (): Promise<
      Lido<ReadonlyMap<string, SupplyAveragePrice> | undefined>
    > => {
      if (codes.length === 0) return { ok: true, valor: new Map() };
      const medias: SupplyAveragePrice[] = [];
      // Um lote de cada vez, como o "Atualizar preços": o compras responde em até 30 s.
      for (const lote of emLotes(codes)) {
        const resultado = await Promise.resolve()
          .then(() =>
            container.getSupplyAveragePrices.execute(
              { projectId, codes: lote, from: periodo.from, to: periodo.to },
              session,
            ),
          )
          .catch(() => undefined);
        if (resultado === undefined) {
          return { ok: false, erro: `O compras não foi consultado — ${SEM_CONEXAO}` };
        }
        if (!resultado.ok) {
          return {
            ok: false,
            erro: `O compras não respondeu: ${messageForCode(resultado.error.code, resultado.error.message)}`,
          };
        }
        // A lista vazia para códigos pedidos é "não há integração" (`GetSupplyAveragePrices`).
        if (resultado.value.length === 0) return { ok: true, valor: undefined };
        medias.push(...resultado.value);
      }
      return { ok: true, valor: mapaDoCompras(medias) };
    };

    const lerFatores = async (): Promise<Lido<ReadonlyMap<string, ProductUnitFactor>>> => {
      if (codes.length === 0) return { ok: true, valor: new Map() };
      const fatores: ProductUnitFactor[] = [];
      for (const lote of emLotes(codes)) {
        const resultado = await Promise.resolve()
          .then(() => container.getProductUnitFactors.execute({ projectId, codes: lote }, session))
          .catch(() => undefined);
        if (resultado === undefined || !resultado.ok) {
          return {
            ok: false,
            erro: `Os fatores já confirmados não foram lidos — ${
              resultado === undefined
                ? SEM_CONEXAO
                : messageForCode(resultado.error.code, resultado.error.message)
            } O fator fica para o preço do item.`,
          };
        }
        fatores.push(...resultado.value);
      }
      return { ok: true, valor: mapaDeFatores(fatores) };
    };

    const lerPrecos = async (): Promise<Lido<ReturnType<typeof mapaDePrecos>>> => {
      const resultado = await Promise.resolve()
        .then(() => container.getProjectMaterialPrices.execute({ projectId }, session))
        .catch(() => undefined);
      if (resultado === undefined || !resultado.ok) {
        return {
          ok: false,
          erro: `Os preços desta obra não foram lidos — ${
            resultado === undefined
              ? SEM_CONEXAO
              : messageForCode(resultado.error.code, resultado.error.message)
          } O provisório fica para o preço do item.`,
        };
      }
      return { ok: true, valor: mapaDePrecos(resultado.value.prices) };
    };

    void Promise.all([lerCompras(), lerFatores(), lerPrecos()]).then(
      ([compras, fatores, precos]) => {
        if (vivo) setLida({ de: daVez, consulta: { compras, fatores, precos } });
      },
    );
    return () => {
      vivo = false;
    };
    // A sessão muda de objeto sem mudar de pessoa: quem decide reler são os produtos e a obra.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [disponivel, temMaterial, produtos, projectId, session?.userId, periodo]);

  const gravar = useCallback(
    async (pedido: PedidoParaOrcar): Promise<void> => {
      if (!disponivel || pedidoVazio(pedido)) return;
      if (container === undefined || session === undefined || projectId === undefined) return;
      const falhas: string[] = [];
      let fatores = 0;
      let provisorios = 0;

      // Uma chamada para todos os fatores (são do produto, e não da obra); mais de 500 só numa
      // planilha que ninguém escreveu ainda, e então em lotes, como o resto.
      for (const lote of emLotes(pedido.fatores)) {
        const resultado = await Promise.resolve()
          .then(() =>
            container.setProductUnitFactors.execute({ projectId, factors: lote }, session),
          )
          .catch(() => undefined);
        if (resultado === undefined) falhas.push(`os fatores não foram gravados — ${SEM_CONEXAO}`);
        else if (!resultado.ok) {
          falhas.push(
            `os fatores não foram gravados: ${messageForCode(resultado.error.code, resultado.error.message)}`,
          );
        } else fatores += lote.length;
      }
      for (const provisorio of pedido.provisorios) {
        const resultado = await Promise.resolve()
          .then(() =>
            container.setMaterialPrice.execute(
              { projectId, key: provisorio.key, provisionalPrice: provisorio.provisionalPrice },
              session,
            ),
          )
          .catch(() => undefined);
        if (resultado?.ok === true) provisorios += 1;
        else if (falhas.every((falha) => !falha.startsWith('o provisório'))) {
          falhas.push(
            `o provisório não foi gravado${
              resultado === undefined
                ? ` — ${SEM_CONEXAO}`
                : `: ${messageForCode(resultado.error.code, resultado.error.message)}`
            }`,
          );
        }
      }

      if (fatores + provisorios > 0) avisarPrecosGravados(projectId);
      const gravado = [
        fatores > 0
          ? `${quantos(fatores, 'fator', 'fatores')} de produto, para todas as obras`
          : undefined,
        provisorios > 0
          ? `${quantos(provisorios, 'provisório', 'provisórios')} nesta obra`
          : undefined,
      ].filter((parte) => parte !== undefined);
      if (falhas.length > 0) {
        toast.error(
          `A importação foi aplicada, mas ${falhas.join('; ')}.${
            gravado.length > 0 ? ` Gravado: ${gravado.join(' e ')}.` : ''
          } O que faltou se completa no preço do item.`,
        );
        return;
      }
      toast.success(`Gravado: ${gravado.join(' e ')}.`);
    },
    [disponivel, container, session, projectId],
  );

  if (!disponivel) return { estado: 'INDISPONIVEL', gravar };
  if (!temMaterial) return { estado: 'PRONTA', gravar };
  return lida !== undefined && lida.de === produtos
    ? { estado: 'PRONTA', consulta: lida.consulta, gravar }
    : { estado: 'LENDO', gravar };
}
